<template>
  <ul class="navbar-nav" v-if="isAuthenticated">
    <!-- Opción de inicio siempre presente -->
    <li class="nav-item" v-if="!hasHomeMenu">
      <router-link class="nav-link" to="/">
        <i class="bi bi-house me-1"></i>Inicio
      </router-link>
    </li>

    <!-- Elementos padre con submenús -->
    <li
      v-for="item in topLevelItems"
      :key="item.key"
      :class="item.type === 'group' ? 'nav-item dropdown' : 'nav-item'"
    >
      <template v-if="item.type === 'group'">
        <a
          class="nav-link dropdown-toggle"
          href="javascript:void(0)"
          role="button"
          :class="{ show: openDropdown === item.key }"
          :aria-expanded="openDropdown === item.key"
          @click.stop="toggleDropdown(item.key)"
        >
          <i v-if="item.parentIcon" :class="'bi bi-' + item.parentIcon + ' me-1'"></i>
          {{ item.parentTitle }}
          <span v-if="pendientesGrupo(item) > 0" class="g3-pend-badge" :title="pendientesGrupo(item) + ' pendiente(s)'">
            {{ pendientesGrupo(item) }}
          </span>
        </a>
        <ul class="dropdown-menu" :class="{ show: openDropdown === item.key }">
          <li v-for="child in item.children" :key="child.MenuId">
            <router-link class="dropdown-item" :to="getRoutePath(child.Enlace)" @click="openDropdown = null">
              <i v-if="child.Imagen" :class="'bi bi-' + child.Imagen + ' me-1'"></i>
              {{ child.Titulo }}
              <span
                v-if="pendientesMenu(child) > 0"
                class="g3-pend-badge"
                :title="'Ver mis ' + pendientesMenu(child) + ' pendiente(s)'"
                @click.prevent.stop="verPendientes(child)"
              >{{ pendientesMenu(child) }}</span>
            </router-link>
          </li>
        </ul>
      </template>

      <router-link v-else class="nav-link" :to="getRoutePath(item.Enlace)">
        <i v-if="item.Imagen" :class="'bi bi-' + item.Imagen + ' me-1'"></i>
        {{ item.Titulo }}
        <span
          v-if="pendientesMenu(item) > 0"
          class="g3-pend-badge"
          :title="'Ver mis ' + pendientesMenu(item) + ' pendiente(s)'"
          @click.prevent.stop="verPendientes(item)"
        >{{ pendientesMenu(item) }}</span>
      </router-link>
    </li>

  </ul>
</template>

<script>
import authService from '@/services/auth';
import docEmpresaConfigService from '@/modules/compras/documentos/services/docEmpresaConfig.service';
import { pendientesState } from '@/shared/services/pendientes.service';

// Solo estos documentos tienen pendientes por usuario
const TIPOS_CON_PENDIENTES = ['REQ', 'SOC'];

function menuOrder(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 999999;
}

function compareMenuOrder(a, b) {
  const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
  return diff !== 0 ? diff : (Number(a.MenuId) || 0) - (Number(b.MenuId) || 0);
}

export default {
  name: 'DynamicMenu',
  data() {
    return {
      menuItems: [],
      menuGroups: [],
      standaloneItems: [],
      topLevelItems: [],
      openDropdown: null,
      pend: pendientesState
    };
  },
  computed: {
    isAuthenticated() {
      return authService.isAuthenticated();
    },
    hasHomeMenu() {
      return this.menuItems.some(item => item.Enlace === '' || item.Enlace === '/');
    }
  },
  created() {
    this.loadMenu();

    // Cerrar dropdown al navegar — guardamos la función de cleanup para no acumular guards
    this._unsubAfter = this.$router.afterEach(() => {
      this.openDropdown = null;
    });
  },
  mounted() {
    // Cerrar dropdown al hacer click fuera del menú
    this._outsideClickHandler = (e) => {
      if (this.$el && !this.$el.contains(e.target)) {
        this.openDropdown = null;
      }
    };
    document.addEventListener('click', this._outsideClickHandler);
  },
  unmounted() {
    // Limpiar listeners para evitar memory leaks
    if (this._unsubAfter) this._unsubAfter();
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler);
    }
  },
  methods: {
    toggleDropdown(key) {
      this.openDropdown = this.openDropdown === key ? null : key;
    },
    tipoConPendientes(item) {
      const enlace = this.getRoutePath(item?.Enlace);
      const tipo = this.docTipoFromMenu({ Enlace: enlace });
      return TIPOS_CON_PENDIENTES.includes(tipo) ? tipo : null;
    },
    pendientesMenu(item) {
      const tipo = this.tipoConPendientes(item);
      return tipo ? Number(this.pend.porTipoDoc?.[tipo] || 0) : 0;
    },
    pendientesGrupo(grupo) {
      const tipos = new Set((grupo.children || []).map(c => this.tipoConPendientes(c)).filter(Boolean));
      let total = 0;
      tipos.forEach(t => { total += Number(this.pend.porTipoDoc?.[t] || 0); });
      return total;
    },
    verPendientes(item) {
      this.openDropdown = null;
      this.$router.push({ path: '/pendientes', query: { tipo: this.tipoConPendientes(item) } });
    },
    getRoutePath(enlace) {
      if (!enlace || enlace === '#') return '/';
      return enlace.startsWith('/') ? enlace : '/' + enlace;
    },
    async loadMenu() {
      // Obtener el menú del usuario desde el servicio de autenticación
      const userMenu = authService.getUserMenu();
      
      if (userMenu && Array.isArray(userMenu)) {
        // Filtrar solo elementos activos y que deben estar en el menú superior
        this.menuItems = await this.filterDocMenusByEmpresaConfig(
          userMenu.filter(item => item.EsActivo === true && item.EsTop === true)
        );
        
        // Identificar elementos padre (que tienen hijos)
        const parentMenuIds = new Set();
        const parentMenuItems = {};
        
        // Primero identificamos todos los IDs de padres mencionados
        this.menuItems.forEach(item => {
          if (item.MenuIdPadre && item.MenuIdPadre > 0) {
            parentMenuIds.add(item.MenuIdPadre);
          }
        });
        
        // Crear grupos de menú basados en la relación padre-hijo
        this.menuGroups = [];
        this.standaloneItems = [];
        this.topLevelItems = [];
        
        // Agrupar elementos por su padre
        const menuItemsByParent = {};
        
        // Inicializar grupos vacíos para cada padre
        parentMenuIds.forEach(parentId => {
          menuItemsByParent[parentId] = [];
        });
        
        // Asignar elementos a sus grupos padre
        this.menuItems.forEach(item => {
          // Si es un elemento con padre y ese padre existe en nuestro conjunto
          if (item.MenuIdPadre && item.MenuIdPadre > 0 && parentMenuIds.has(item.MenuIdPadre)) {
            // Añadir al grupo de su padre
            if (!menuItemsByParent[item.MenuIdPadre]) {
              menuItemsByParent[item.MenuIdPadre] = [];
            }
            menuItemsByParent[item.MenuIdPadre].push(item);
            
            // Guardar referencia al elemento padre si no lo tenemos aún
            if (!parentMenuItems[item.MenuIdPadre]) {
              // Buscar el elemento padre en la lista de menús
              const parentItem = this.menuItems.find(parent => parent.MenuId === item.MenuIdPadre);
              if (parentItem) {
                parentMenuItems[item.MenuIdPadre] = parentItem;
              }
            }
          } 
          // Si es un elemento padre (tiene hijos)
          else if (parentMenuIds.has(item.MenuId)) {
            // Guardar referencia al elemento padre
            parentMenuItems[item.MenuId] = item;
          }
          // Si no es padre ni tiene padre, es un elemento independiente
          else if (!item.MenuIdPadre || item.MenuIdPadre === 0) {
            this.standaloneItems.push(item);
          }
        });
        
        // Crear los grupos finales con título del padre e hijos ordenados
        Object.keys(menuItemsByParent).forEach(parentId => {
          const children = menuItemsByParent[parentId];
          const parentItem = parentMenuItems[parentId];
          
          if (children.length > 0 && parentItem) {
            // Ordenar los hijos por el campo Orden
            const sortedChildren = children.sort(compareMenuOrder);
            
            this.menuGroups.push({
              type: 'group',
              key: `group-${parentItem.MenuId}`,
              parentId: parentId,
              MenuId: parentItem.MenuId,
              Orden: parentItem.Orden,
              parentTitle: parentItem.Titulo,
              parentIcon: parentItem.Imagen,
              order: parentItem.Orden,
              children: sortedChildren
            });
          }
        });
        
        // Ordenar los grupos por el campo "Orden" del padre
        this.menuGroups.sort(compareMenuOrder);
        
        // Ordenar los elementos independientes por Orden
        this.standaloneItems.sort(compareMenuOrder);

        this.topLevelItems = [
          ...this.menuGroups,
          ...this.standaloneItems.map(item => ({
            ...item,
            type: 'standalone',
            key: `standalone-${item.MenuId}`
          }))
        ].sort(compareMenuOrder);
      } else {
        this.menuItems = [];
        this.menuGroups = [];
        this.standaloneItems = [];
        this.topLevelItems = [];
      }
    },
    async filterDocMenusByEmpresaConfig(items) {
      if (!authService.getSelectedEmpresa()) return items;
      try {
        const config = await docEmpresaConfigService.leer();
        const activeDocs = new Set(
          (config.Items || [])
            .filter(x => x.Activo === true || x.Activo === undefined)
            .map(x => String(x.TipoDocumento || '').toUpperCase())
        );
        if (activeDocs.size === 0) return items;

        return items.filter(item => {
          const tipo = this.docTipoFromMenu(item);
          return !tipo || activeDocs.has(tipo);
        });
      } catch (err) {
        console.warn('No se pudo filtrar menu por configuracion de documentos', err);
        return items;
      }
    },
    docTipoFromMenu(item) {
      const path = String(item?.Enlace || '').trim().toLowerCase();
      const map = {
        '/compras/req': 'REQ',
        '/compras/soc': 'SOC',
        '/compras/scot': 'SCOT',
        '/compras/cotcom': 'COTP',
        '/compras/oc': 'OC',
        '/compras/em': 'ENTM',
        '/compras/fac': 'FACP',
        '/compras/deve': 'DEVP',
        '/ventas/cot': 'COTV',
        '/ventas/ove': 'OV',
        '/ventas/entiven': 'REM',
        '/ventas/fcve': 'FACV',
        '/ventas/deve': 'DEVV'
      };
      return map[path] || null;
    }
  }
};
</script>
